import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { Appointment, CreateOrUpdateAppointmentDTO } from '../models/appointment.model';
import { INavAppointmentsTab } from '../widgets/nav-tabs/mp-nav-tab.model';
import { RealTimeService } from './realtime.service';
import { AbstractService } from './abstract.service';
import { Filter } from '../models/filter.model';
import PaginationParams from '../widgets/lazy-loading/pagination-params.model';
import { ConcurrentResourceRegistryService } from '../realtime/concurrent-resource-registry/concurrent-resource-registry.service';
import { ConcurrentResourceRegistry,
  ConcurrentResourceType } from '../realtime/concurrent-resource-registry/concurrent-resource.model';

@Injectable({ providedIn: 'root' })
export class AppointmentsService extends AbstractService<Appointment> {

  constructor(
    injector: Injector,
      private readonly realTimeService: RealTimeService,
      private readonly concurrentResourceRegistryService: ConcurrentResourceRegistryService
  ) {
    super(injector);
  }

  public get(tab: INavAppointmentsTab, paginationParams: Partial<PaginationParams>, filters: Filter[] = [], cancelRequest$: Observable<void> = of()) {
    return this.getAllLazy(`appointments${tab.path}`, filters, paginationParams, cancelRequest$);
  }

  public getById(appointmentId: string) {
    const url = `${environment.api_base_url}appointments/${appointmentId}`;
    return this.http.get(url);
  }

  public create(appointment: CreateOrUpdateAppointmentDTO) {
    const url = `${environment.api_base_url}appointments`;
    return this.http.post(url, appointment);
  }

  public update(appointment: CreateOrUpdateAppointmentDTO) {
    const url = `${environment.api_base_url}appointments/${appointment.id}`;
    return this.http.patch(url, appointment);
  }

  public duplicate(appointmentId: string) {
    const url = `${environment.api_base_url}appointments/${appointmentId}/duplicate`;
    return this.http.post(url, appointmentId);
  }

  public delete(appointmentId: string) {
    const url = `${environment.api_base_url}appointments/${appointmentId}`;
    return this.http.delete(url);
  }

  public onUpdatedAppointment(): Observable<Appointment> {
    return this.realTimeService.watch<Appointment>('appointment-updated');
  }

  public onDeletedAppointment(): Observable<string> {
    return this.realTimeService.watch<string>('appointment-deleted');
  }

  public onAppointmentAssigned(): Observable<string> {
    return this.realTimeService.watch<string>('appointment-assigned');
  }

  public lockAppointments(...appointments: Appointment[]) {
    const resourcesLocked = appointments.map((appointment: Appointment) => Appointment.mapToConcurrentResource(appointment));
    this.concurrentResourceRegistryService.lock(...resourcesLocked);
  }

  public unlockAppointments(...appointments: Appointment[]) {
    const resourcesLocked = appointments.map((appointment: Appointment) => Appointment.mapToConcurrentResource(appointment));
    this.concurrentResourceRegistryService.unlock(...resourcesLocked);
  }

  public getLockedAppointmentsRegistry$(): Observable<ConcurrentResourceRegistry> {
    return this.concurrentResourceRegistryService.getResourcesLockedByType$(ConcurrentResourceType.APPOINTMENT);
  }

  public getLockedAppointmentsRegistry(): ConcurrentResourceRegistry {
    return this.concurrentResourceRegistryService.getRegistryByType(ConcurrentResourceType.APPOINTMENT);
  }

}
